import { type RefObject, useLayoutEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const outerHeight = (el: HTMLElement | null): number => {
  if (!el) return 0;
  const style = getComputedStyle(el);
  return el.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
};

export const useTableHeight = (ref: RefObject<Element | null>) => {
  // keep the table the height of the parent.
  const [tableHeight, setTableHeight] = useState<number>();

  const resizeTable = useDebouncedCallback(
    () => {
      const node = ref.current;
      if (!node) {
        return;
      }
      const { height } = node.getBoundingClientRect();
      const header = node.querySelector<HTMLElement>('.ant-table-header');
      const pagination = node.querySelector<HTMLElement>('.ant-table-pagination');
      const overhead = outerHeight(header) + outerHeight(pagination);
      const scrollHeight = height - overhead;
      setTableHeight(scrollHeight);
    },
    100,
    {
      trailing: true,
      maxWait: 100,
    }
  );

  useLayoutEffect(() => {
    const node = ref.current;
    resizeTable();
    window.addEventListener('resize', resizeTable);

    // Re-measure when DOM changes (e.g. pagination appears after data loads)
    const observer = node ? new MutationObserver(resizeTable) : null;
    observer?.observe(node!, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', resizeTable);
      observer?.disconnect();
    };
  }, [resizeTable, ref]);

  return tableHeight;
};
